import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Bot, ClipboardList, FileText, Files, Layers, ListChecks, Maximize2, MessageSquarePlus, Minimize2, Paperclip, Pencil, Search, Send, Sparkles, Square, Trash2, X } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { bytes, relative } from '../../lib/format';
import type { Tone } from '../../lib/labels';
import { Badge, Button, ErrorBanner, useConfirm, useToast } from '../ui';
import type { AssistantDraft, AssistantStatus } from '../../pages/staff/AssistantPage';
import { ActionCard, CopyReply, MessageDraftCard, type AssistantAction, type MessageDraft } from './ActionCards';
import { pageRefFor, useAssistant, type PageRef } from './AssistantContext';
import { DraftCard } from './DraftCard';
import { Markdown } from './Markdown';

/**
 * The assistant panel, for Circdal staff and for the reps of an agency Circdal turned it on for (queue item 7): a
 * floating button at the bottom right of every portal page opens a panel
 * with the person's chats and tasks, the conversation and a side pane for files and results. It can fill the screen,
 * and on a phone it is a full-screen sheet. On a project, quote or design page it shows a chip naming that page, and
 * the page's record goes with the next message unless the chip is removed. The reply streams in as it is written and
 * is drawn from its Markdown (Markdown.tsx), with nothing from the model ever put in as HTML.
 *
 * It answers as the signed-in person with their permissions and never sends email. Part 2: a change it proposes shows
 * as a confirm card (ActionCards.tsx) and is made only on Confirm; drafted messages show with Copy; longer work runs as
 * a background task in the Tasks tab, which polls while a task runs and survives a reload; and the quick actions
 * "What needs me" and "Update this project" sit on an empty chat.
 */

type Attachment = { id: number; name: string; mimeType: string; sizeBytes: number; url: string };
type Starter = 'draft_bid' | 'what_needs_me' | 'project_update' | 'project_concept';
type Message = {
  id: number | string;
  role: 'user' | 'assistant' | 'event';
  text: string;
  starter?: Starter;
  context?: { type: string; id: number; label: string };
  tools?: { name: string; input: unknown; ok: boolean }[];
  draft?: AssistantDraft;
  drafts?: MessageDraft[];
  actions?: AssistantAction[];
  startedTasks?: { id: number; title: string }[];
  stopped?: string;
  /** The person's answer on whether this message's files could go to the AI model. */
  fileConsent?: { decision: 'send' | 'withhold'; by: string; at: string; files: string[] };
  attachments: Attachment[];
  costCents: number;
  createdAt?: string;
  pending?: boolean;
};
/** Files of a message waiting for the person's answer before anything goes to the AI model (Trent, 2026-11-02). */
type FileQuestion = { chatId: number; files: { name: string; sizeBytes: number }[]; token: string; text: string; starter: Starter | null; context: PageRef | null; tempUser: string; tempReply: string };
type Chat = { id: number; title: string; link: PageRef | null; updatedAt: string };
type Task = {
  id: number;
  title: string;
  prompt: string;
  status: 'queued' | 'running' | 'done' | 'failed' | 'stopped';
  progress: string | null;
  result: string | null;
  error: string | null;
  tools: { name: string; ok: boolean }[];
  chatId: number | null;
  context: { label?: string } | null;
  costCents: number;
  createdAt: string;
  finishedAt: string | null;
  actions: AssistantAction[];
};
const TASK_STATUS: Record<Task['status'], { label: string; tone: Tone }> = {
  queued: { label: 'Waiting', tone: '' },
  running: { label: 'Running', tone: 'sky' },
  done: { label: 'Done', tone: 'green' },
  failed: { label: 'Failed', tone: 'red' },
  stopped: { label: 'Stopped', tone: 'warn' },
};
const isLive = (t: Task) => t.status === 'queued' || t.status === 'running';

const MAX_FILES = 3;
const MAX_BYTES = 10 * 1024 * 1024;
/** Customer models and CAD drawings stay in the portal, so they may be larger. */
const MAX_MODEL_BYTES = 25 * 1024 * 1024;
const isModel = (name: string) => /\.(3dm|obj|stl|dxf)$/i.test(name);
const ACCEPT = 'application/pdf,image/png,image/jpeg,image/webp,image/gif,text/plain,text/markdown,text/csv,.txt,.eml,.md,.csv,.3dm,.obj,.stl,.dxf';
/**
 * The public, client and sign-in pages never show the assistant, even to a signed-in staff member, and neither does
 * Preview as client, which shows a design as the client sees it.
 */
const OUTSIDE = /^\/(login|invite|forgot|inquire|workshop|approve|share|client)(\/|$)|^\/projects\/\d+\/designs\/\d+\/preview\/?$/;

const TOOL_LABEL: Record<string, string> = {
  search_projects: 'Searched projects',
  get_project: 'Opened a project',
  list_my_tasks: 'Listed your tasks',
  get_quote: 'Opened a quote',
  get_design: 'Opened a design',
  list_recent_activity: 'Read recent activity',
  get_production_settings: 'Read production settings and prices',
  get_prices: 'Read the prices',
  what_needs_me: 'Listed what needs you',
  create_task: 'Prepared a to-do',
  update_task: 'Prepared a change to a to-do',
  add_project_note: 'Prepared a project note',
  update_project: 'Prepared a project update',
  create_contact: 'Prepared a contact',
  draft_message: 'Drafted a message',
  start_task: 'Started a task',
  create_design: 'Prepared a design',
  make_concept: 'Drew a concept',
  inspect_model: 'Read a model',
  add_design_to_quote: 'Prepared a quote draft',
};
const cost = (c: number) => `$${(c / 100).toFixed(2)}`;

/**
 * The floating button and the panel, on every portal page of a person who has the assistant: staff, and a rep whose
 * agency has it on. The server refuses everyone else, so a rep whose agency has it off sees no button.
 */
export function AssistantDock() {
  const { hasAssistant } = useAuth();
  const location = useLocation();
  if (!hasAssistant || OUTSIDE.test(location.pathname)) return null;
  return <Dock />;
}

/** What GET /assistant/status says to the panel. */
type PanelStatus = Pick<AssistantStatus, 'enabled' | 'reason' | 'person'> & { rep: boolean; features: { tasks: boolean; draftBid: boolean } };

function Dock() {
  const { isOpen, open, close } = useAssistant();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);
  useEffect(() => {
    // Focus goes back to the button when the panel closes.
    if (wasOpen.current && !isOpen) buttonRef.current?.focus();
    wasOpen.current = isOpen;
  }, [isOpen]);
  return (
    <>
        <style>{CSS}</style>
        {isOpen ? null : (
          <button ref={buttonRef} type="button" className="ap-fab" aria-label="Open the assistant" title="Assistant" onClick={() => open()}>
            <Sparkles />
          </button>
        )}
        {isOpen ? <Panel onClose={close} /> : null}
    </>
  );
}

/** Reads the server-sent events of a reply as they arrive. */
async function readEvents(res: Response, on: (event: string, data: any) => void) {
  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let i: number;
    while ((i = buf.indexOf('\n\n')) >= 0) {
      const chunk = buf.slice(0, i);
      buf = buf.slice(i + 2);
      const ev = /^event: (.+)$/m.exec(chunk)?.[1];
      const data = /^data: (.+)$/m.exec(chunk)?.[1];
      if (ev && data) on(ev, JSON.parse(data));
    }
  }
}

function Panel({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const location = useLocation();
  const { request, consume, close } = useAssistant();
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const toast = useToast();
  const { isStaff } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [pane, setPane] = useState<'none' | 'chats' | 'files'>('none');
  /** Which list the left pane shows. */
  const [left, setLeft] = useState<'chats' | 'tasks'>('chats');
  /** A background task open in the main area instead of the chat. */
  const [taskView, setTaskView] = useState<number | null>(null);
  const [taskText, setTaskText] = useState('');
  const [taskBusy, setTaskBusy] = useState(false);
  const [chatId, setChatId] = useState<number | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loadingChat, setLoadingChat] = useState(false);
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [starter, setStarter] = useState<'draft_bid' | null>(null);
  const [busy, setBusy] = useState(false);
  const [fileQuestion, setFileQuestion] = useState<FileQuestion | null>(null);
  const [toolNote, setToolNote] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [renaming, setRenaming] = useState<number | null>(null);
  const [renameText, setRenameText] = useState('');

  const status = useQuery({ queryKey: ['assistant-status', 'panel'], queryFn: () => api.get<PanelStatus>('/assistant/status') });
  /** Background tasks and "Draft a bid from an email" are for Circdal staff; a rep's panel leaves them out. */
  const forRep = status.data?.rep ?? !isStaff;
  const hasTasks = status.data?.features.tasks ?? isStaff;
  const hasDraftBid = status.data?.features.draftBid ?? isStaff;
  const chats = useQuery({ queryKey: ['assistant-chats'], queryFn: () => api.get<{ chats: Chat[] }>('/assistant/chats') });
  // The tasks poll every two seconds while one is waiting or running, so progress and the result show without a reload.
  const tasks = useQuery({
    queryKey: ['assistant-tasks'],
    queryFn: () => api.get<{ tasks: Task[] }>('/assistant/tasks'),
    refetchInterval: (q) => (q.state.data?.tasks.some(isLive) ? 2000 : false),
    enabled: hasTasks,
  });
  const seenLive = useRef<Set<number>>(new Set());
  useEffect(() => {
    for (const t of tasks.data?.tasks ?? []) {
      if (isLive(t)) seenLive.current.add(t.id);
      else if (seenLive.current.has(t.id)) {
        seenLive.current.delete(t.id);
        toast(t.status === 'done' ? `Your task is done: ${t.title}` : `Your task ${t.status === 'stopped' ? 'stopped' : 'failed'}: ${t.title}`);
        void qc.invalidateQueries({ queryKey: ['notifications'] });
        void qc.invalidateQueries({ queryKey: ['assistant-status'] });
      }
    }
  }, [tasks.data, toast, qc]);

  /* The page chip: the project, quote or design this page shows, unless the person removed it. */
  const ref = pageRefFor(location.pathname);
  const refKey = ref ? `${ref.type}:${ref.id}` : '';
  const [removedChip, setRemovedChip] = useState<string | null>(null);
  const chip = useQuery({
    queryKey: ['assistant-context', refKey],
    queryFn: () => api.get<{ context: PageRef & { label: string } }>(`/assistant/context?type=${ref!.type}&id=${ref!.id}`),
    enabled: !!ref,
    retry: false,
    staleTime: 60_000,
  });
  const chipCtx = ref && removedChip !== refKey && chip.data ? chip.data.context : null;

  const enabled = status.data?.enabled ?? false;
  /** Known to be off or over a cap. While the status loads, the box can be typed in but not sent. */
  const blocked = !!status.data && !status.data.enabled;
  const capReason = status.data && !status.data.enabled ? status.data.reason : null;

  /* Focus: into the panel when it opens, trapped inside while open; Escape closes it. */
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  // Escape closes an open list first, then the panel. It is heard on the document so it still works when the focused
  // control has just been hidden, but not while focus is in a dialog outside the panel, such as "Delete this chat?".
  useEffect(() => {
    const onEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const active = document.activeElement;
      if (active && active !== document.body && !panelRef.current?.contains(active)) return;
      if (pane !== 'none' && !(expanded && window.innerWidth > 640)) {
        setPane('none');
        inputRef.current?.focus();
      } else onClose();
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [pane, expanded, onClose]);
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !panelRef.current) return;
    const focusable = [...panelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type=file]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter(
      (el) => el.offsetParent !== null || el === document.activeElement,
    );
    if (!focusable.length) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  useLayoutEffect(() => {
    endRef.current?.scrollIntoView?.({ block: 'end' });
  }, [messages, toolNote]);

  const openChat = useCallback(async (id: number) => {
    setPane('none');
    setTaskView(null);
    setError(null);
    setChatId(id);
    setStarter(null);
    setLoadingChat(true);
    try {
      const res = await api.get<{ messages: Message[] }>(`/assistant/chats/${id}`);
      setMessages(res.messages);
    } catch (err) {
      setError(err);
    } finally {
      setLoadingChat(false);
    }
  }, []);

  /** Reads the chat again, quietly, after a card was confirmed or cancelled, so the note of it shows. */
  const reloadChat = useCallback(async (id: number | null) => {
    if (!id) return;
    try {
      const res = await api.get<{ messages: Message[] }>(`/assistant/chats/${id}`);
      setMessages(res.messages);
    } catch {
      // the card already shows the result
    }
  }, []);

  const newChat = () => {
    setTaskView(null);
    setChatId(null);
    setMessages([]);
    setStarter(null);
    setError(null);
    setPane('none');
    inputRef.current?.focus();
  };

  /** Posts a message and streams the reply into the two placeholder messages. Returns the server's file question, if any. */
  const postMessage = useCallback(
    async (id: number, fd: FormData, tempUserId: string, tempReplyId: string): Promise<{ files: { name: string; sizeBytes: number }[]; token: string } | null> => {
      const res = await fetch(`/api/assistant/chats/${id}/messages`, { method: 'POST', credentials: 'same-origin', body: fd });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        // The files are kept in the portal and wait for the person's answer before anything goes to the AI model.
        if (res.status === 409 && body?.code === 'confirm_files') return { files: body.files ?? [], token: body.pendingFiles };
        throw new ApiError(res.status, body?.error ?? `The request failed (${res.status}).`, body?.code);
      }
      let failed: string | null = null;
      await readEvents(res, (event, data) => {
        if (event === 'start') setMessages((m) => m.map((x) => (x.id === tempUserId ? { ...data.userMessage } : x)));
        else if (event === 'text') setMessages((m) => m.map((x) => (x.id === tempReplyId ? { ...x, text: x.text + data.delta } : x)));
        else if (event === 'tool') setToolNote(data.status === 'running' ? `${TOOL_LABEL[data.name] ?? data.name}...` : null);
        else if (event === 'done') {
          setMessages((m) => m.map((x) => (x.id === tempReplyId ? { ...data.message } : x)));
          if (data.message.startedTasks?.length) {
            for (const t of data.message.startedTasks as { id: number }[]) seenLive.current.add(t.id);
            void qc.invalidateQueries({ queryKey: ['assistant-tasks'] });
          }
        } else if (event === 'error') failed = data.error;
      });
      if (failed) {
        setMessages((m) => m.filter((x) => x.id !== tempReplyId));
        throw new Error(failed);
      }
      return null;
    },
    [qc],
  );

  const formFor = (o: { text: string; starter: Starter | null; context: PageRef | null }) => {
    const fd = new FormData();
    fd.append('text', o.text);
    if (o.context) fd.append('context', JSON.stringify({ type: o.context.type, id: o.context.id }));
    if (o.starter) fd.append('starter', o.starter);
    return fd;
  };

  /** Sends a message, creating the chat first when this is a new one, and streams the reply in. */
  const send = useCallback(
    async (opts: { text: string; files: File[]; starter: Starter | null; context: PageRef | null; fresh?: boolean }) => {
      setTaskView(null);
      setBusy(true);
      setError(null);
      setToolNote(null);
      setFileQuestion(null);
      const tempUser: Message = { id: `u${Date.now()}`, role: 'user', text: opts.text, starter: opts.starter ?? undefined, attachments: opts.files.map((f, i) => ({ id: -i, name: f.name, mimeType: f.type, sizeBytes: f.size, url: '' })), costCents: 0, pending: true };
      const tempReply: Message = { id: `a${Date.now()}`, role: 'assistant', text: '', attachments: [], costCents: 0, pending: true };
      setMessages((m) => [...(opts.fresh ? [] : m), tempUser, tempReply]);
      let asking = false;
      try {
        let id = opts.fresh ? null : chatId;
        if (!id) {
          id = (await api.post<{ chat: Chat }>('/assistant/chats', opts.context ? { context: opts.context } : {})).chat.id;
          setChatId(id);
        }
        const fd = formFor(opts);
        for (const f of opts.files) fd.append('file', f);
        const question = await postMessage(id, fd, tempUser.id as string, tempReply.id as string);
        if (question) {
          asking = true;
          setFileQuestion({ chatId: id, files: question.files, token: question.token, text: opts.text, starter: opts.starter, context: opts.context, tempUser: tempUser.id as string, tempReply: tempReply.id as string });
        }
      } catch (err) {
        setMessages((m) => m.filter((x) => x.id !== tempReply.id || x.text));
        setError(err);
      } finally {
        setBusy(false);
        setToolNote(null);
        if (!asking) {
          qc.invalidateQueries({ queryKey: ['assistant-chats'] });
          qc.invalidateQueries({ queryKey: ['assistant-status'] });
        }
      }
    },
    [chatId, qc, postMessage],
  );

  /** The person's answer to the file question: the message goes on with its files, or without their contents. */
  const answerFiles = useCallback(
    async (decision: 'send' | 'withhold') => {
      const q = fileQuestion;
      if (!q) return;
      setFileQuestion(null);
      setBusy(true);
      setError(null);
      try {
        const fd = formFor(q);
        fd.append('pendingFiles', q.token);
        fd.append('filesConsent', decision);
        await postMessage(q.chatId, fd, q.tempUser, q.tempReply);
      } catch (err) {
        setMessages((m) => m.filter((x) => x.id !== q.tempReply || x.text));
        setError(err);
      } finally {
        setBusy(false);
        setToolNote(null);
        qc.invalidateQueries({ queryKey: ['assistant-chats'] });
        qc.invalidateQueries({ queryKey: ['assistant-status'] });
      }
    },
    [fileQuestion, postMessage, qc],
  );

  /* A page asked for a new chat with a question, such as the quote page's "Explain these with the assistant". */
  const handled = useRef<number | null>(null);
  useEffect(() => {
    if (!request || handled.current === request.key) return;
    if (request.taskId) {
      handled.current = request.key;
      consume();
      setTaskView(request.taskId);
      setLeft('tasks');
      void qc.invalidateQueries({ queryKey: ['assistant-tasks'] });
      return;
    }
    if (!request.ask || busy || !status.data) return;
    handled.current = request.key;
    consume();
    setChatId(null);
    if (!status.data.enabled) {
      setMessages([]);
      return;
    }
    void send({ text: request.ask, files: [], starter: null, context: request.context ?? null, fresh: true });
  }, [request, busy, status.data, consume, send, qc]);

  /** A quick action: "What needs me", or "Update this project" and "Concept for this project" on a project page. */
  const QUICK_TEXT = { what_needs_me: 'What needs me', project_update: 'Update this project', project_concept: 'Concept for this project' } as const;
  const quick = (which: keyof typeof QUICK_TEXT) => {
    if (busy || !enabled) return;
    if (which !== 'what_needs_me' && chipCtx?.type !== 'project') return;
    void send({ text: QUICK_TEXT[which], files: [], starter: which, context: chipCtx ? { type: chipCtx.type, id: chipCtx.id } : null });
  };

  const startTask = async () => {
    const prompt = taskText.trim();
    if (!prompt || taskBusy) return;
    setTaskBusy(true);
    setError(null);
    try {
      const res = await api.post<{ task: Task }>('/assistant/tasks', { prompt, context: chipCtx ? { type: chipCtx.type, id: chipCtx.id } : null });
      setTaskText('');
      // watched from the start, so its end is announced even when it finishes before the next poll
      seenLive.current.add(res.task.id);
      setTaskView(res.task.id);
      setPane('none');
      await qc.invalidateQueries({ queryKey: ['assistant-tasks'] });
    } catch (err) {
      setError(err);
    } finally {
      setTaskBusy(false);
    }
  };
  const stopTask = async (t: Task) => {
    try {
      await api.post(`/assistant/tasks/${t.id}/stop`);
      await qc.invalidateQueries({ queryKey: ['assistant-tasks'] });
    } catch (err) {
      setError(err);
    }
  };
  /** On a phone or full screen, following a link closes the panel so the page shows. */
  const navClose = () => (window.innerWidth <= 640 || expanded) && close();

  const submit = () => {
    if (busy || !enabled) return;
    const t = text.trim();
    if (!t && !files.length) return;
    if (starter === 'draft_bid' && t.length < 20 && !files.length) {
      setError(new Error('Paste the email or the brief, or attach a PDF or an image.'));
      return;
    }
    setText('');
    setFiles([]);
    const s = starter;
    setStarter(null);
    void send({ text: t, files, starter: s, context: chipCtx ? { type: chipCtx.type, id: chipCtx.id } : null });
  };

  const addFiles = (list: FileList | null) => {
    const picked = [...(list ?? [])];
    const limit = (f: File) => (isModel(f.name) ? MAX_MODEL_BYTES : MAX_BYTES);
    const tooBig = picked.find((f) => f.size > limit(f));
    if (tooBig) setError(new Error(`${tooBig.name} is larger than ${isModel(tooBig.name) ? '25' : '10'} MB.`));
    const next = [...files, ...picked.filter((f) => f.size <= limit(f))];
    if (next.length > MAX_FILES) setError(new Error('Attach at most three files.'));
    setFiles(next.slice(0, MAX_FILES));
  };

  const rename = async (id: number) => {
    const title = renameText.trim();
    setRenaming(null);
    if (!title) return;
    try {
      await api.patch(`/assistant/chats/${id}`, { title });
      qc.invalidateQueries({ queryKey: ['assistant-chats'] });
    } catch (err) {
      setError(err);
    }
  };
  const remove = async (c: Chat) => {
    if (!(await confirm(`Delete the chat "${c.title}"? Its messages are deleted for good.`, 'Delete chat'))) return;
    try {
      await api.del(`/assistant/chats/${c.id}`);
      if (c.id === chatId) newChat();
      qc.invalidateQueries({ queryKey: ['assistant-chats'] });
    } catch (err) {
      setError(err);
    }
  };

  const current = chats.data?.chats.find((c) => c.id === chatId);
  const allFiles = messages.flatMap((m) => m.attachments.filter((a) => a.url));
  const drafts = messages.filter((m) => m.draft);
  const lookups = messages.flatMap((m) => m.tools ?? []);
  const cards = messages.flatMap((m) => m.actions ?? []);

  const taskList = (
    <section className="ap-tasks" aria-label="Tasks">
      <form
        className="ap-task-new"
        onSubmit={(e) => {
          e.preventDefault();
          void startTask();
        }}
      >
        <textarea
          className="textarea"
          rows={3}
          aria-label="Describe a task"
          placeholder="Longer work, such as: summarize every open project and list what needs me this week."
          value={taskText}
          disabled={blocked}
          onChange={(e) => setTaskText(e.target.value)}
        />
        <button type="submit" className="btn sm primary" disabled={!enabled || taskBusy || taskText.trim().length < 10}>
          <ListChecks /> Start task
        </button>
      </form>
      {tasks.data?.tasks.length ? (
        <ul>
          {tasks.data.tasks.map((t) => (
            <li key={t.id} className={t.id === taskView ? 'active' : ''}>
              <button type="button" className="ap-chat-open" onClick={() => (setTaskView(t.id), setPane('none'))} aria-current={t.id === taskView ? 'true' : undefined}>
                <span className="ap-chat-title">{t.title}</span>
                <span className="tiny muted">
                  {TASK_STATUS[t.status].label}
                  {t.actions.some((a) => a.status === 'proposed') ? ', waiting for you to confirm' : ''} · {relative(t.createdAt)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="tiny muted" style={{ padding: '4px 6px' }}>
          Tasks run in the background, so you can keep working. You get a notification when one is done.
        </p>
      )}
    </section>
  );

  const chatList = (
    <nav className="ap-chats-list" aria-label="Chats">
      <button type="button" className="btn sm primary ap-new" onClick={newChat}>
        <MessageSquarePlus /> New chat
      </button>
      {chats.data?.chats.length ? (
        <ul>
          {chats.data.chats.map((c) => (
            <li key={c.id} className={c.id === chatId ? 'active' : ''}>
              {renaming === c.id ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void rename(c.id);
                  }}
                >
                  <input className="input" aria-label="Chat name" value={renameText} autoFocus onChange={(e) => setRenameText(e.target.value)} onBlur={() => void rename(c.id)} />
                </form>
              ) : (
                <>
                  <button type="button" className="ap-chat-open" onClick={() => void openChat(c.id)} aria-current={c.id === chatId ? 'true' : undefined}>
                    <span className="ap-chat-title">{c.title}</span>
                    <span className="tiny muted">{relative(c.updatedAt)}</span>
                  </button>
                  <button type="button" className="btn ghost icon sm" aria-label={`Rename ${c.title}`} onClick={() => (setRenaming(c.id), setRenameText(c.title))}>
                    <Pencil />
                  </button>
                  <button type="button" className="btn ghost icon sm" aria-label={`Delete ${c.title}`} onClick={() => void remove(c)}>
                    <Trash2 />
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="tiny muted" style={{ padding: '4px 6px' }}>
          Your chats are kept here. Only you can see them.
        </p>
      )}
    </nav>
  );

  const leftPane = !hasTasks ? (
    <div className="ap-chats">{chatList}</div>
  ) : (
    <div className="ap-chats">
      <div className="ap-tabs" role="tablist" aria-label="Chats and tasks">
        <button type="button" role="tab" aria-selected={left === 'chats'} className={left === 'chats' ? 'on' : ''} onClick={() => setLeft('chats')}>
          Chats
        </button>
        <button type="button" role="tab" aria-selected={left === 'tasks'} className={left === 'tasks' ? 'on' : ''} onClick={() => setLeft('tasks')}>
          Tasks{tasks.data?.tasks.some(isLive) ? ` (${tasks.data.tasks.filter(isLive).length} running)` : ''}
        </button>
      </div>
      {left === 'chats' ? chatList : taskList}
    </div>
  );

  const openTask = tasks.data?.tasks.find((t) => t.id === taskView) ?? null;
  const taskPane = taskView ? (
    <div className="ap-messages ap-task-view" aria-live="polite">
      <button type="button" className="btn ghost sm" onClick={() => setTaskView(null)}>
        <ArrowLeft /> Back to the chat
      </button>
      {!openTask ? (
        <p className="small muted">{tasks.isLoading ? 'Loading the task.' : 'This task was not found.'}</p>
      ) : (
        <article aria-label={`Task: ${openTask.title}`} className="ap-task">
          <header className="ap-task-head">
            <h3>{openTask.title}</h3>
            <Badge tone={TASK_STATUS[openTask.status].tone}>{TASK_STATUS[openTask.status].label}</Badge>
          </header>
          <p className="tiny muted">
            Started {relative(openTask.createdAt)}
            {openTask.context?.label ? ` on ${openTask.context.label}` : ''}. {openTask.prompt}
          </p>
          {isLive(openTask) ? (
            <div className="row wrap">
              <span className="small">
                <span className="spinner" style={{ width: 12, height: 12, verticalAlign: -1 }} /> {openTask.progress ?? (openTask.status === 'queued' ? 'Waiting to start.' : 'Working.')}
              </span>
              <button type="button" className="btn sm" onClick={() => void stopTask(openTask)}>
                <Square /> Stop
              </button>
            </div>
          ) : null}
          {openTask.result ? (
            <div className="ap-msg assistant">
              <Markdown text={openTask.result} onNavigate={navClose} />
              <div className="ap-cost tiny muted">
                <CopyReply text={openTask.result} /> This task cost {cost(openTask.costCents)}.
              </div>
            </div>
          ) : null}
          {openTask.error ? <p className="ap-cap small">{openTask.error}</p> : null}
          {openTask.actions.map((a) => (
            <ActionCard key={a.id} action={a} onNavigate={navClose} onChange={() => void qc.invalidateQueries({ queryKey: ['assistant-tasks'] })} />
          ))}
          {openTask.chatId ? (
            <button type="button" className="btn ghost sm" onClick={() => void openChat(openTask.chatId!)}>
              Open the chat it was started from
            </button>
          ) : null}
        </article>
      )}
    </div>
  ) : null;

  const sidePane = (
    <aside className="ap-side" aria-label="Files and results">
      <h3>Files</h3>
      {allFiles.length ? (
        <ul>
          {allFiles.map((a) => (
            <li key={a.id}>
              <a href={a.url} target="_blank" rel="noreferrer">
                <FileText /> {a.name}
              </a>{' '}
              <span className="tiny muted">{bytes(a.sizeBytes)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="tiny muted">Files you attach in this chat are listed here.</p>
      )}
      <h3>Results</h3>
      {drafts.length || lookups.length || cards.length ? (
        <ul>
          {cards.map((a) => (
            <li key={`a${a.id}`}>
              <Pencil /> {a.preview.title}: {a.status === 'proposed' ? 'waiting for you' : a.status === 'confirmed' ? 'done' : a.status === 'cancelled' ? 'cancelled' : 'not made'}
            </li>
          ))}
          {drafts.map((m) => (
            <li key={m.id}>
              <Sparkles /> Draft bid: {m.draft!.projectName ?? m.draft!.quoteTitle}
            </li>
          ))}
          {lookups.map((t, i) => (
            <li key={i} className={t.ok ? '' : 'muted'}>
              <Search /> {TOOL_LABEL[t.name] ?? t.name}
              {t.ok ? '' : ' (not found)'}
            </li>
          ))}
        </ul>
      ) : (
        <p className="tiny muted">Draft bids, proposed changes and what the assistant looked up are listed here.</p>
      )}
    </aside>
  );

  return (
    <>
    <div
      ref={panelRef}
      className={`ap-panel ${expanded ? 'expanded' : ''} pane-${pane}`}
      role="dialog"
      aria-modal="true"
      aria-label="Assistant"
      onKeyDown={onKeyDown}
    >
      <header className="ap-head">
        <button
          type="button"
          className={`btn ghost icon sm ${pane === 'chats' && left === 'chats' ? 'on' : ''} ap-toggle-chats`}
          aria-label="Chats"
          aria-expanded={pane === 'chats' && left === 'chats'}
          onClick={() => {
            setLeft('chats');
            setPane((p) => (p === 'chats' && left === 'chats' ? 'none' : 'chats'));
          }}
        >
          <Bot />
        </button>
        <div className="ap-title">
          <strong>{current?.title ?? 'Assistant'}</strong>
          <span className="tiny muted">
            {forRep
              ? 'Answers as you, with your access to your own projects. It asks before any change, and it never sends email.'
              : 'Answers as you, with your access. It asks before any change, and it never sends email.'}
          </span>
        </div>
        {hasTasks ? (
          <button
            type="button"
            className={`btn ghost icon sm ${pane === 'chats' && left === 'tasks' ? 'on' : ''} ap-toggle-chats`}
            aria-label="Tasks"
            aria-expanded={pane === 'chats' && left === 'tasks'}
            onClick={() => {
              setLeft('tasks');
              setPane((p) => (p === 'chats' && left === 'tasks' ? 'none' : 'chats'));
            }}
          >
            <ClipboardList />
          </button>
        ) : null}
        <button type="button" className="btn ghost icon sm" aria-label="New chat" onClick={newChat}>
          <MessageSquarePlus />
        </button>
        <button type="button" className={`btn ghost icon sm ${pane === 'files' ? 'on' : ''} ap-toggle-files`} aria-label="Files and results" aria-expanded={pane === 'files'} onClick={() => setPane((p) => (p === 'files' ? 'none' : 'files'))}>
          <Files />
        </button>
        <button type="button" className="btn ghost icon sm ap-expand" aria-label={expanded ? 'Shrink the assistant' : 'Expand the assistant to full screen'} onClick={() => setExpanded((x) => !x)}>
          {expanded ? <Minimize2 /> : <Maximize2 />}
        </button>
        <button type="button" className="btn ghost icon sm" aria-label="Close the assistant" onClick={onClose}>
          <X />
        </button>
      </header>
      <div className="ap-body">
        {leftPane}
        <section className="ap-convo" aria-label="Conversation">
          {taskPane}
          <div className="ap-messages" aria-live="polite" hidden={!!taskView}>
            {loadingChat ? <p className="small muted">Loading the chat.</p> : null}
            {!messages.length && !loadingChat ? (
              <div className="ap-empty">
                {forRep ? (
                  <p>Ask about your projects, your designs, your tasks or the prices the portal shows you. It can add a to-do or a note, move a project to its next phase, make a configurator design or a concept checked against the LFAM rules, after you confirm. You can attach a PDF, an image, a saved email or a customer model (.3dm, .obj, .stl or .dxf), which stays in the portal.</p>
                ) : (
                  <p>Ask about a project, a quote, a design, your tasks or the current prices. It can add a to-do or a note, update a project, make a configurator design or a concept checked against the LFAM rules, after you confirm. You can attach a PDF, an image, a saved email or a customer model (.3dm, .obj, .stl or .dxf), which stays in the portal.</p>
                )}
                <div className="ap-quick">
                  <button type="button" className="btn sm" disabled={blocked || busy} onClick={() => quick('what_needs_me')}>
                    <ListChecks /> What needs me
                  </button>
                  {chipCtx?.type === 'project' ? (
                    <button type="button" className="btn sm" disabled={blocked || busy} onClick={() => quick('project_update')}>
                      <ClipboardList /> Update this project
                    </button>
                  ) : null}
                  {chipCtx?.type === 'project' ? (
                    <button type="button" className="btn sm" disabled={blocked || busy} onClick={() => quick('project_concept')}>
                      <Layers /> Concept for this project
                    </button>
                  ) : null}
                  {hasDraftBid ? (
                    <button type="button" className="btn sm" disabled={blocked} onClick={() => (setStarter('draft_bid'), inputRef.current?.focus())}>
                      <Sparkles /> Draft a bid from an email
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}
            {messages.map((m) =>
              m.role === 'event' ? (
                <div key={m.id} className="ap-event tiny" role="note">
                  {m.text}
                </div>
              ) : (
              <div key={m.id} className={`ap-msg ${m.role}`}>
                {m.role === 'user' && m.context ? <div className="tiny muted">On {m.context.label}</div> : null}
                {m.role === 'user' && m.starter === 'draft_bid' ? <div className="tiny muted">Draft a bid from this</div> : null}
                {m.role === 'user' && m.starter && m.starter !== 'draft_bid' ? <div className="tiny muted">Quick action</div> : null}
                {m.text ? (
                  m.role === 'assistant' ? (
                    <Markdown text={m.text} onNavigate={navClose} />
                  ) : (
                    <div className="ap-text">{m.text}</div>
                  )
                ) : m.pending && m.role === 'assistant' ? (
                  <div className="ap-text muted">{toolNote ?? 'Thinking...'}</div>
                ) : null}
                {m.attachments.length ? (
                  <div className="ap-atts">
                    {m.attachments.map((a) =>
                      a.url ? (
                        <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="ap-att">
                          <Paperclip /> {a.name}
                        </a>
                      ) : (
                        <span key={a.id} className="ap-att">
                          <Paperclip /> {a.name}
                        </span>
                      ),
                    )}
                  </div>
                ) : null}
                {m.role === 'user' && m.fileConsent ? (
                  <div className="tiny muted ap-consent-note" role="note">
                    {m.fileConsent.decision === 'send'
                      ? `${m.fileConsent.by} agreed to send ${m.fileConsent.files.join(', ')} to the AI model.`
                      : `${m.fileConsent.by} chose not to send ${m.fileConsent.files.join(', ')} to the AI model, so the assistant did not read ${m.fileConsent.files.length === 1 ? 'it' : 'them'}.`}
                  </div>
                ) : null}
                {m.draft ? <DraftCard draft={m.draft} onNavigate={navClose} /> : null}
                {m.drafts?.map((d, i) => <MessageDraftCard key={i} draft={d} />)}
                {m.actions?.map((a) => (
                  <ActionCard key={a.id} action={a} onNavigate={navClose} onChange={() => void reloadChat(chatId)} />
                ))}
                {m.startedTasks?.map((t) => (
                  <button key={t.id} type="button" className="ap-chip task" onClick={() => (setTaskView(t.id), setLeft('tasks'))}>
                    <ListChecks /> Task started: {t.title}. Open it.
                  </button>
                ))}
                {m.role === 'assistant' && m.pending && m.text && toolNote ? <div className="tiny muted">{toolNote}</div> : null}
                {m.role === 'assistant' && !m.pending ? (
                  <div className="ap-cost tiny muted">
                    {m.text ? <CopyReply text={m.text} /> : null}
                    {m.tools?.length ? `${[...new Set(m.tools.map((t) => TOOL_LABEL[t.name] ?? t.name))].join(', ')}. ` : ''}
                    {m.costCents ? `This reply cost ${cost(m.costCents)}.` : 'This reply cost nothing.'}
                  </div>
                ) : null}
              </div>
              ),
            )}
            {fileQuestion ? (
              <section className="ap-action consent" role="alertdialog" aria-label="Send the files to the AI model?" aria-describedby="ap-files-q">
                <div className="ap-action-head">
                  <strong>Send the files to the AI model?</strong>
                </div>
                <p id="ap-files-q" className="small" style={{ margin: 0 }}>
                  The files you attached will be sent to an AI model to read. Are any of them confidential? Is it okay to send them?
                </p>
                <ul className="small ap-consent-files">
                  {fileQuestion.files.map((f, i) => (
                    <li key={i}>
                      <Paperclip /> {f.name}
                    </li>
                  ))}
                </ul>
                <div className="row wrap">
                  <Button variant="primary" size="sm" onClick={() => void answerFiles('send')}>
                    Send them
                  </Button>
                  <Button size="sm" onClick={() => void answerFiles('withhold')}>
                    Don't send the files
                  </Button>
                </div>
                <p className="tiny muted" style={{ margin: 0 }}>
                  Nothing has gone to the AI model yet. Don't send the files sends your message without them.
                </p>
              </section>
            ) : null}
            <div ref={endRef} />
          </div>
          <form
            className="ap-composer"
            hidden={!!taskView}
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            {capReason ? <p className="ap-cap small">{capReason}</p> : null}
            <ErrorBanner error={error} />
            <div className="ap-chips">
              {chipCtx ? (
                <span className="ap-chip">
                  {chipCtx.label}
                  <button type="button" aria-label={`Remove ${chipCtx.label}`} onClick={() => setRemovedChip(refKey)}>
                    <X />
                  </button>
                </span>
              ) : null}
              {starter === 'draft_bid' ? (
                <span className="ap-chip draft">
                  Drafting a bid
                  <button type="button" aria-label="Stop drafting a bid" onClick={() => setStarter(null)}>
                    <X />
                  </button>
                </span>
              ) : null}
              {files.map((f, i) => (
                <span key={`${f.name}${i}`} className="ap-chip file">
                  <Paperclip /> {f.name}
                  <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles((x) => x.filter((_, j) => j !== i))}>
                    <X />
                  </button>
                </span>
              ))}
            </div>
            <div className="ap-input">
              <textarea
                ref={inputRef}
                className="textarea"
                rows={starter === 'draft_bid' ? 6 : 2}
                aria-label="Message the assistant"
                placeholder={starter === 'draft_bid' ? 'Paste the email or the brief here, or attach a PDF or an image.' : 'Ask the assistant'}
                value={text}
                disabled={blocked}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey && starter !== 'draft_bid') {
                    e.preventDefault();
                    submit();
                  }
                }}
              />
              <div className="ap-actions">
                <input ref={fileRef} type="file" multiple accept={ACCEPT} hidden onChange={(e) => (addFiles(e.target.files), (e.target.value = ''))} aria-label="Attach files" />
                <button type="button" className="btn ghost icon sm" aria-label="Attach a PDF, an image, a text file or a model" disabled={!enabled || files.length >= MAX_FILES} onClick={() => fileRef.current?.click()}>
                  <Paperclip />
                </button>
                <button type="submit" className="btn primary icon sm" aria-label="Send" disabled={!enabled || busy || (!text.trim() && !files.length)}>
                  {busy ? <span className="spinner" style={{ width: 14, height: 14 }} /> : <Send />}
                </button>
              </div>
            </div>
            {status.data?.person ? (
              <p className="tiny muted ap-spend">
                You have used {cost(status.data.person.cents)} of your {cost(status.data.person.capCents)} this month. Up to three files of 10 MB each, or 25 MB for a model.
              </p>
            ) : null}
          </form>
        </section>
        {sidePane}
      </div>
    </div>
    {dialog}
    </>
  );
}

const CSS = `
.ap-fab { position: fixed; right: 20px; bottom: 20px; z-index: 50; width: 52px; height: 52px; border-radius: 999px; border: 0; background: var(--emerald); color: #fff; display: grid; place-items: center; box-shadow: var(--shadow-lg); cursor: pointer; }
.ap-fab:hover { background: var(--emerald-700); }
.ap-fab:focus-visible { outline: 3px solid var(--orange); outline-offset: 2px; }
.ap-fab svg { width: 22px; height: 22px; }
.ap-panel { position: fixed; right: 16px; bottom: 16px; top: 16px; z-index: 58; width: min(560px, calc(100vw - 32px)); background: var(--white); border: 1px solid var(--line); border-radius: 14px; box-shadow: var(--shadow-lg); display: flex; flex-direction: column; overflow: hidden; }
.ap-panel.expanded { inset: 0; width: auto; border-radius: 0; border: 0; }
.ap-head { display: flex; align-items: center; gap: 4px; padding: 8px 10px; border-bottom: 1px solid var(--line); background: var(--cream); }
.ap-head .btn.on { background: var(--emerald-soft); }
.ap-title { flex: 1; min-width: 0; display: grid; }
.ap-title strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ap-title .tiny { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ap-body { flex: 1; min-height: 0; display: grid; grid-template-columns: minmax(0, 1fr); position: relative; }
.ap-chats, .ap-side { position: absolute; top: 0; bottom: 0; width: min(280px, 85%); background: var(--white); z-index: 2; overflow-y: auto; padding: 10px; display: none; box-shadow: var(--shadow-lg); }
.ap-chats { left: 0; border-right: 1px solid var(--line); }
.ap-side { right: 0; border-left: 1px solid var(--line); }
.pane-chats .ap-chats, .pane-files .ap-side { display: block; }
.ap-panel.expanded .ap-body { grid-template-columns: 260px minmax(0, 1fr) 280px; }
.ap-panel.expanded .ap-chats, .ap-panel.expanded .ap-side { position: static; display: block; width: auto; box-shadow: none; }
.ap-panel.expanded .ap-toggle-chats, .ap-panel.expanded .ap-toggle-files { display: none; }
.ap-new { width: 100%; justify-content: center; margin-bottom: 8px; }
.ap-chats ul, .ap-side ul { list-style: none; margin: 0; padding: 0; display: grid; gap: 2px; }
.ap-chats li { display: flex; align-items: center; gap: 2px; border-radius: var(--radius-sm); }
.ap-chats li.active { background: var(--emerald-tint); }
.ap-chats li form { flex: 1; }
.ap-chat-open { flex: 1; min-width: 0; text-align: left; background: none; border: 0; padding: 6px; display: grid; cursor: pointer; color: inherit; font: inherit; }
.ap-chat-title { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13px; }
.ap-side h3 { font-size: 13px; margin: 6px 0; }
.ap-side li { font-size: 13px; overflow-wrap: anywhere; }
.ap-side svg { width: 14px; height: 14px; vertical-align: -2px; }
.ap-convo { display: flex; flex-direction: column; min-height: 0; min-width: 0; }
.ap-messages { flex: 1; overflow-y: auto; padding: 14px; display: flex; flex-direction: column; gap: 12px; }
.ap-panel.expanded .ap-messages { padding: 18px max(14px, calc((100% - 760px) / 2)); }
.ap-empty { display: grid; gap: 10px; justify-items: start; color: var(--ink-soft); }
.ap-empty p { margin: 0; }
.ap-msg { display: grid; gap: 6px; max-width: 100%; min-width: 0; }
.ap-msg.user { justify-self: end; max-width: 88%; background: var(--emerald-tint); border-radius: 12px; padding: 8px 12px; }
.ap-text { white-space: pre-wrap; overflow-wrap: anywhere; }
.ap-atts { display: flex; flex-wrap: wrap; gap: 6px; }
.ap-att { display: inline-flex; align-items: center; gap: 4px; font-size: 12px; background: var(--white); border: 1px solid var(--line); border-radius: 999px; padding: 2px 8px; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ap-att svg { width: 12px; height: 12px; flex: none; }
.ap-cost { margin-top: -2px; }
.ap-draft { display: grid; gap: 10px; border: 1px solid var(--line); border-radius: var(--radius); padding: 12px; background: var(--white); min-width: 0; }
.ap-draft-head { font-weight: 600; }
.ap-draft .kv { margin: 0; }
.ap-list { margin: 6px 0 0; padding-left: 18px; }
.ap-firms { display: grid; gap: 6px; }
.ap-composer { border-top: 1px solid var(--line); padding: 10px 12px 12px; display: grid; gap: 6px; background: var(--white); }
.ap-panel.expanded .ap-composer { padding-left: max(12px, calc((100% - 760px) / 2)); padding-right: max(12px, calc((100% - 760px) / 2)); }
.ap-cap { margin: 0; color: var(--warn); background: var(--warn-soft); border-radius: var(--radius-sm); padding: 6px 10px; }
.ap-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.ap-chips:empty { display: none; }
.ap-chip { display: inline-flex; align-items: center; gap: 4px; background: var(--sky-soft); border-radius: 999px; padding: 2px 4px 2px 10px; font-size: 12px; max-width: 100%; min-width: 0; }
.ap-chip.draft { background: var(--orange-soft); }
.ap-chip.file { background: var(--cream-deep); }
.ap-chip svg { width: 12px; height: 12px; flex: none; }
.ap-chip button { border: 0; background: none; display: grid; place-items: center; padding: 2px; cursor: pointer; border-radius: 999px; color: inherit; }
.ap-input { display: flex; gap: 6px; align-items: flex-end; }
.ap-consent-files { margin: 0; padding-left: 0; list-style: none; display: grid; gap: 2px; overflow-wrap: anywhere; }
.ap-consent-files svg { width: 13px; height: 13px; vertical-align: -2px; }
.ap-input .textarea { flex: 1; min-width: 0; resize: vertical; max-height: 40vh; }
.ap-actions { display: flex; gap: 4px; }
.ap-spend { margin: 0; }
.ap-messages[hidden], .ap-composer[hidden] { display: none; }
.ap-tabs { display: flex; gap: 2px; margin-bottom: 8px; border-bottom: 1px solid var(--line); }
.ap-tabs button { flex: 1; background: none; border: 0; border-bottom: 2px solid transparent; padding: 6px 4px; font: inherit; font-size: 13px; cursor: pointer; color: var(--ink-soft); }
.ap-tabs button.on { color: inherit; border-bottom-color: var(--emerald); font-weight: 600; }
.ap-tasks ul { list-style: none; margin: 8px 0 0; padding: 0; display: grid; gap: 2px; }
.ap-tasks li { border-radius: var(--radius-sm); display: flex; }
.ap-tasks li.active { background: var(--emerald-tint); }
.ap-task-new { display: grid; gap: 6px; }
.ap-task-new .textarea { resize: vertical; font-size: 13px; }
.ap-task-new .btn { justify-self: start; }
.ap-task { display: grid; gap: 10px; min-width: 0; }
.ap-task-head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.ap-task-head h3 { margin: 0; font-size: 16px; }
.ap-task-view > .btn { justify-self: start; }
.ap-quick { display: flex; flex-wrap: wrap; gap: 6px; }
.ap-event { align-self: center; text-align: center; color: var(--ink-soft); background: var(--cream); border-radius: 999px; padding: 3px 12px; max-width: 92%; }
.ap-cost { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
.ap-copy { width: 24px; height: 24px; padding: 0; }
.ap-copy svg { width: 13px; height: 13px; }
.ap-chip.task { background: var(--emerald-soft); border: 0; cursor: pointer; padding: 3px 10px; font: inherit; font-size: 12px; justify-self: start; }
.ap-action { display: grid; gap: 8px; border: 1px solid var(--line); border-left: 3px solid var(--orange); border-radius: var(--radius); padding: 10px 12px; background: var(--white); min-width: 0; }
.ap-action.confirmed { border-left-color: var(--emerald); }
.ap-action.cancelled, .ap-action.failed { border-left-color: var(--line); }
.ap-action.draft { border-left-color: var(--sky); }
.ap-action-head { display: flex; flex-wrap: wrap; align-items: baseline; gap: 4px 10px; }
.ap-action-note, .ap-action-status { margin: 0; }
.ap-concept { display: block; border: 1px solid var(--line); border-radius: var(--radius-sm); overflow: hidden; background: var(--cream); }
.ap-concept img { display: block; width: 100%; height: auto; }
.ap-checks summary { cursor: pointer; font-weight: 500; }
.ap-checks ul { list-style: none; margin: 6px 0 0; padding: 0; display: grid; gap: 4px; font-size: 13px; }
.ap-check { padding-left: 8px; border-left: 3px solid var(--emerald); overflow-wrap: anywhere; }
.ap-check.warn { border-left-color: var(--orange); }
.ap-check.fail { border-left-color: var(--red, #d22b2b); }
.ap-check-word { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; color: var(--ink-soft); }
.ap-action.failed .ap-action-status { color: var(--warn); }
.ap-changes { width: 100%; border-collapse: collapse; font-size: 13px; table-layout: fixed; }
.ap-changes th, .ap-changes td { text-align: left; vertical-align: top; padding: 3px 6px 3px 0; overflow-wrap: anywhere; white-space: pre-wrap; }
.ap-changes thead th { font-size: 11px; color: var(--ink-soft); font-weight: 500; }
.ap-changes tbody th { width: 34%; color: var(--ink-soft); font-weight: 500; }
.ap-changes td.before { color: var(--ink-soft); text-decoration: line-through; }
.ap-md { overflow-wrap: anywhere; min-width: 0; line-height: 1.5; }
.ap-md > :first-child { margin-top: 0; }
.ap-md > :last-child { margin-bottom: 0; }
.ap-md p, .ap-md ul, .ap-md ol, .ap-md pre, .ap-md blockquote, .ap-md .ap-md-table { margin: 0 0 8px; }
.ap-md h3, .ap-md h4, .ap-md h5, .ap-md h6 { margin: 12px 0 6px; line-height: 1.3; }
.ap-md h3 { font-size: 15px; }
.ap-md h4, .ap-md h5, .ap-md h6 { font-size: 14px; }
.ap-md ul, .ap-md ol { padding-left: 20px; }
.ap-md li + li { margin-top: 2px; }
.ap-md li > ul, .ap-md li > ol { margin: 2px 0 0; }
.ap-md code { font-family: var(--font-mono); font-size: 12px; background: var(--cream); border-radius: 4px; padding: 1px 4px; }
.ap-md pre { background: var(--cream); border-radius: var(--radius-sm); padding: 8px 10px; overflow-x: auto; white-space: pre; }
.ap-md pre code { background: none; padding: 0; }
.ap-md blockquote { border-left: 3px solid var(--line); padding-left: 10px; color: var(--ink-soft); }
.ap-md hr { border: 0; border-top: 1px solid var(--line); margin: 10px 0; }
.ap-md-table { overflow-x: auto; max-width: 100%; }
.ap-md table { border-collapse: collapse; font-size: 13px; }
.ap-md th, .ap-md td { border: 1px solid var(--line); padding: 4px 8px; text-align: left; vertical-align: top; }
.ap-md th { background: var(--cream); font-weight: 600; }
@media (max-width: 640px) {
  .ap-panel, .ap-panel.expanded { inset: 0; width: 100%; border-radius: 0; border: 0; }
  .ap-panel.expanded .ap-body { grid-template-columns: minmax(0, 1fr); }
  .ap-panel.expanded .ap-chats, .ap-panel.expanded .ap-side { position: absolute; display: none; width: min(280px, 85%); box-shadow: var(--shadow-lg); }
  .ap-panel.expanded.pane-chats .ap-chats, .ap-panel.expanded.pane-files .ap-side { display: block; }
  .ap-panel.expanded .ap-toggle-chats, .ap-panel.expanded .ap-toggle-files { display: inline-flex; }
  .ap-expand { display: none; }
  .ap-title .tiny { display: none; }
  .ap-fab { right: 14px; bottom: 14px; }
}
`;
